import React, { FC } from 'react';

import Picture from 'components/picture';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { parseStringifiedJSON } from 'utils/parse-string';

import { PriceAndCalories } from './price-and-calories';
import SelectionsList from './selections-list';
import { ProductSkeleton } from './skeleton';
import {
  DetailsContainer,
  ImageContainer as ImageContainerBase,
  Layout,
  ProductDescription,
  ProductName,
  QuantityBadge,
} from './styled';
import { ProductProps } from './types';

/**
 *
 * Product is used to display a compact product to guests
 * Anywhere we need to show a menu item outside the menu we should use this component
 *
 */
export const Product: FC<React.PropsWithChildren<ProductProps>> = ({
  image,
  fallbackImage,
  name,
  price,
  calories,
  note,
  selections,
  expandable = true,
  quantity,
  loading = false,
  displayMode = 'list',
  imageSize,
  isProductNameBold = false,
  description,
  quantityBadgeTextStyle,
  quantityBadgeStyle,
}) => {
  // Should not render the product if the name is empty and is not loading
  if (!name && !loading) {
    return null;
  }

  const isInlineLayout = displayMode === 'list' || !!selections?.length;

  return (
    <>
      {loading ? (
        <ProductSkeleton />
      ) : (
        <Layout $inline={isInlineLayout} testID="product">
          {(image || fallbackImage) && (
            <ImageContainerBase key={name} $size={imageSize} testID="product-image">
              <Picture
                image={parseStringifiedJSON({ value: image || '' })}
                alt={`${t('product')} ${name}`}
                accessibilityHidden
                objectFitContain
              />
              {quantity && (
                <QuantityBadge {...quantityBadgeStyle}>
                  <Typography
                    variant="copyTwo"
                    style={quantityBadgeTextStyle}
                    {...hiddenAccessibilityPlatformProps}
                  >
                    {quantity}x
                  </Typography>
                  <VisuallyHidden accessibilityLabel={t('xItems', { xItems: quantity })} />
                </QuantityBadge>
              )}
            </ImageContainerBase>
          )}
          <DetailsContainer>
            {note}
            <ProductName testID="product-name" $isProductNameBold={isProductNameBold}>
              {name}
            </ProductName>
            {description && <ProductDescription>{description}</ProductDescription>}
            <PriceAndCalories price={price} calories={calories} />
            {selections && (
              <SelectionsList selections={selections} id={name || ''} expandable={expandable} />
            )}
          </DetailsContainer>
        </Layout>
      )}
    </>
  );
};
